import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft } from "lucide-react";
import { SITE } from "@/lib/siteConfig";

const EASE = [0.22, 1, 0.36, 1];

// Parse ONLY the non-private appointment fields Calendly appends to the redirect
// URL. Never read invitee email/phone/uuid/guests/custom answers.
function parseAppointment(search) {
  const params = new URLSearchParams(search || "");
  const typeName = params.get("event_type_name");
  const startRaw = params.get("event_start_time");
  const endRaw = params.get("event_end_time");

  const start = startRaw ? new Date(startRaw) : null;
  const end = endRaw ? new Date(endRaw) : null;
  const startValid = start && !Number.isNaN(start.getTime());
  const endValid = end && !Number.isNaN(end.getTime());

  // No usable details -> render the normal thank-you page.
  if (!startValid && !typeName) return null;

  let dateStr = null;
  let timeStr = null;
  let tzName = null;

  if (startValid) {
    dateStr = new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(start);

    const timeFmt = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    const startTime = timeFmt.format(start);
    const endTime = endValid ? timeFmt.format(end) : null;
    timeStr = endTime ? `${startTime}\u2013${endTime}` : startTime;

    // Visitor's local time zone (generic long name, e.g. "Eastern Time").
    try {
      const parts = new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        timeZoneName: "longGeneric",
      }).formatToParts(start);
      const tz = parts.find((p) => p.type === "timeZoneName");
      tzName = tz ? tz.value : null;
    } catch (e) {
      tzName = null;
    }
  }

  return { typeName: typeName || null, dateStr, timeStr, tzName };
}

export default function ThankYou() {
  const appointment = useMemo(
    () =>
      parseAppointment(
        typeof window !== "undefined" ? window.location.search : ""
      ),
    []
  );

  // Confirmation pages should not be indexed by search engines.
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    let created = false;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
      created = true;
    }
    const prevRobots = meta.getAttribute("content");
    meta.setAttribute("content", "noindex, nofollow");

    const prevTitle = document.title;
    document.title = "Your Consultation Is Scheduled | Brown & Associates";

    return () => {
      if (created) {
        document.head.removeChild(meta);
      } else {
        meta.setAttribute("content", prevRobots);
      }
      document.title = prevTitle;
    };
  }, []);

  // Google Ads appointment conversion — fires once when /thank-you loads.
  useEffect(() => {
    if (window.__consultationConversionFired) return;
    window.__consultationConversionFired = true;
    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: "AW-18359105612/BrpBCNvv7NgcEMzwprJE",
      });
    }
  }, []);


  return (
    <div className="min-h-[100svh] flex flex-col bg-navy-900 text-warm-neutral antialiased overflow-hidden">
      {/* Minimal header — wordmark only, no scheduling action */}
      <header className="container-page w-full h-20 flex items-center">
        <Link
          to="/"
          data-testid="thankyou-logo"
          aria-label={`${SITE.shortName} — home`}
          className="flex flex-col leading-none"
        >
          <span className="font-heading text-2xl md:text-[1.7rem] font-semibold tracking-tight text-warm-neutral">
            Brown &amp; Associates
          </span>
          <span className="font-body text-[0.6rem] uppercase tracking-[0.32em] mt-1 text-warm-accent">
            Bookkeeping &middot; Manchester, NH
          </span>
        </Link>
      </header>

      <div className="grain-overlay" aria-hidden="true" />

      <main className="flex-1 flex items-center relative z-10">
        <div className="container-page py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="max-w-2xl"
          >
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-warm-accent/20 text-warm-accent">
              <Check className="h-7 w-7" aria-hidden="true" />
            </span>

            <h1
              data-testid="thankyou-headline"
              className="font-heading font-semibold text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight mt-8"
            >
              Your Consultation Is{" "}
              <span className="italic font-normal text-warm-accent">Scheduled</span>
            </h1>

            {appointment && (
              <div
                data-testid="appointment-details"
                className="mt-8 rounded-lg border border-warm-neutral/20 bg-navy-800/50 p-6"
              >
                {appointment.typeName && (
                  <p
                    data-testid="appointment-type"
                    className="font-body text-xs uppercase tracking-[0.22em] text-warm-accent"
                  >
                    {appointment.typeName}
                  </p>
                )}
                {appointment.dateStr && (
                  <p
                    data-testid="appointment-date"
                    className="font-heading text-2xl md:text-3xl text-warm-neutral mt-2 leading-tight"
                  >
                    {appointment.dateStr}
                  </p>
                )}
                {appointment.timeStr && (
                  <p
                    data-testid="appointment-time"
                    className="font-body text-base sm:text-lg text-warm-neutral/80 mt-1"
                  >
                    {appointment.timeStr}
                    {appointment.tzName ? ` ${appointment.tzName}` : ""}
                  </p>
                )}
              </div>
            )}

            <p className="font-body text-base sm:text-lg leading-relaxed text-warm-neutral/80 mt-6">
              Thank you for scheduling a consultation with {SITE.shortName}. You
              will receive confirmation and appointment details from Calendly by
              email.
            </p>

            <p className="font-body text-base sm:text-lg leading-relaxed text-warm-neutral/70 mt-4">
              I look forward to learning more about your business and your
              bookkeeping concerns.
            </p>

            <div className="mt-10">
              <Link
                to="/"
                data-testid="thankyou-return-button"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full font-body font-semibold tracking-wide px-8 py-4 text-base md:text-lg bg-warm-neutral text-navy-900 transition-colors duration-300 hover:bg-white"
              >
                <ArrowLeft
                  className="h-[1.05em] w-[1.05em] transition-transform duration-300 group-hover:-translate-x-0.5"
                  aria-hidden="true"
                />
                <span>Return to Bookkeeping Checkup</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
